/**
 * Detects when a newer build has been deployed than the one this tab is running,
 * so the app can offer a "refresh to update" prompt (UpdateAvailableToast).
 *
 * How: the running bundle bakes in its commit SHA (src/version.ts, COMMIT_SHA).
 * Each build also writes /version.json (served no-cache). We poll that file on an
 * interval + whenever the tab regains focus, and compare. A mismatch means a new
 * deploy has landed while this tab stayed open (or the tab is on a stale cached
 * bundle) — either way, a refresh gets the user onto the current version.
 *
 * Fail-safe: never prompts on dev/local builds, and swallows fetch errors
 * (offline / transient) rather than nagging.
 */
import { useCallback, useEffect, useState } from 'react';
import { COMMIT_SHA } from '../version';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — new versions aren't urgent

/** A build whose identity we can't meaningfully compare (local dev / unset env). */
function isDevBuild(sha: string): boolean {
  return !sha || sha === 'local' || sha === 'dev';
}

/** Pure comparison: is `fetched` a different (newer) build than `current`? */
export function isNewVersion(current: string, fetched?: string | null): boolean {
  if (!fetched) return false; // no/bad data → don't prompt
  if (isDevBuild(current)) return false; // dev build → never prompt
  return fetched !== current;
}

export function useVersionCheck(): { updateAvailable: boolean } {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const current = COMMIT_SHA;

  const check = useCallback(async () => {
    if (updateAvailable || isDevBuild(current)) return; // already prompting / dev
    try {
      const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { commit?: string };
      if (isNewVersion(current, data.commit)) setUpdateAvailable(true);
    } catch {
      /* offline / fetch failed — ignore, try again next tick */
    }
  }, [current, updateAvailable]);

  useEffect(() => {
    if (isDevBuild(current)) return; // no polling in dev
    const id = window.setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    check(); // one check shortly after load (catches a stale cached bundle)
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [check, current]);

  return { updateAvailable };
}
